import { useMemo, useCallback, memo } from "react";
import Table, { TableRow } from "../../components/common/Table/Table";
import CardTable from "../../components/common/CardTable";
import TextAreaWithCounter from "../../components/common/TextAreaWithCounter";
import RatingTag from "./components/RatingTag";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import type { RootState } from "../../store/store";
import {
  AssessmentData,
  assessmentColumns,
  setHasUnsavedChanges,
  updateAssessment,
} from "../../store/slices/pathToPerformanceSlice";
import { 
  MAX_NOTES_LENGTH,
  ASSESSMENT_PERIODS,
  FIELD_IDS,
  ARIA_LABELS,
} from "../../constants/pathToPerformance";
import { hasAssessmentsChanged } from "../../utils/PathToPerformance/p2pValidation";
import { toInteger } from "../../utils/typeGuards";

interface AssessmentTableProps {
  data: AssessmentData[];
  readOnly?: boolean;
}

const AssessmentTable = memo<AssessmentTableProps>(({ 
  data, 
  readOnly = false 
}) => {
  const dispatch = useAppDispatch();
  const { initialAssessments } = useAppSelector((state: RootState) => state.pathToPerformance);

  /**
   * Handle item update - directly update Redux state
   * Only sets hasUnsavedChanges if the new value differs from initial data
   * useCallback ensures stable reference for memoization
   */
  const handleItemUpdate = useCallback((id: string, updates: Partial<AssessmentData>) => {
    dispatch(updateAssessment({ id, data: updates }));
    
    // Create updated assessments array to check for changes
    const updatedAssessments = data.map(item => 
      item.id === id ? { ...item, ...updates } : item
    );
    
    // Only set hasUnsavedChanges if there are actual changes from initial data
    const hasChanges = hasAssessmentsChanged(updatedAssessments, initialAssessments);
    dispatch(setHasUnsavedChanges(hasChanges));
  }, [dispatch, data, initialAssessments]);

  /**
   * Create stable handler for assessment notes change
   * Prevents inline function creation in JSX
   */
  const createNotesChangeHandler = useCallback((rowId: string) => (value: string) => {
    handleItemUpdate(rowId, { assessmentNotes: value });
  }, [handleItemUpdate]);

  /**
   * Transform data to table rows
   * Memoized with all dependencies to prevent unnecessary recalculation
   */
  const rows: TableRow[] = useMemo(
    () =>
      data.map((row) => {
        // Convert teamRating to number with type guard - safe conversion
        const rating = toInteger(row.teamRating, 0);

        // Check if this is the previous period (read-only)
        const isPrevious = row.period.toLowerCase() === ASSESSMENT_PERIODS.PREVIOUS.toLowerCase();

        return {
          period: row.period,
          teamRating: rating > 0 ? (
            <RatingTag rating={rating} />
          ) : (
            <span className="text-gray-400">-</span>
          ),
          assessmentDate: row.assessmentDate || "-",
          assessmentNotes: (
            <TextAreaWithCounter
              className="text-[16px]!"
              id={FIELD_IDS.ASSESSMENT_NOTES(row.id)}
              label="Assessment Notes"
              value={row.assessmentNotes}
              placeholder="Enter assessment notes"
              maxLength={MAX_NOTES_LENGTH}
              ariaLabel={ARIA_LABELS.ASSESSMENT_NOTES_TEXTAREA}
              onChange={createNotesChangeHandler(row.id)}
              readOnly={isPrevious || readOnly}
            />
          ),
          id: row.id,
        };
      }),
    [data, readOnly, createNotesChangeHandler]
  );

  return (
    <div className="w-full">
      <div className="w-full mb-4!">
        <h2 className="h2 mt-15! mb-6!">Assessments</h2>
        {
         !readOnly &&  <p className="p2 mb-6!">Before leaving, select "save" to keep your changes. </p>
        }
  
      </div>
      <CardTable>
        {rows.length > 0 ? (
          <Table columns={assessmentColumns} rows={rows}  />
        ) : (
          <p className="p3 -book text-center mt-2.5! desktop:mt-50!">
            No performance data available.
          </p>
        )}
      </CardTable>
    </div>
  );
});

AssessmentTable.displayName = "AssessmentTable";

export default AssessmentTable;
